import { tEnglish as t } from "@buza/i18n";
import type { ApiResult } from "./api.js";

// The staff screen error pattern, parlour side (NFR-15: S, L, CR, P, F and M screens follow the
// ZD errors pattern of spec 4.3; decision BD-63, BD-68). The same behaviour as the console's
// zd/refusal.tsx: the refusing rule's reason and ID, field reasons beside their fields, entries
// kept (the caller keeps its form state), the stale message with a Reload button, and the
// no-connection message. The server audits every refused attempt.

export interface Refusal {
  kind: "rule" | "stale" | "offline";
  message: string;
  rule?: string;
  fields: Record<string, string>;
}

export function toRefusal<T>(r: ApiResult<T>): Refusal | undefined {
  if (r.ok) return undefined;
  if (r.status === 0) return { kind: "offline", message: t("common.errors.no_connection"), fields: {} };
  const e = r.error;
  if (e?.code === "STALE_RECORD") return { kind: "stale", message: t("common.errors.stale_record"), rule: e.rule, fields: {} };
  return { kind: "rule", message: e?.message ?? t("common.errors.validation"), rule: e?.rule, fields: e?.fields ?? {} };
}

/** The If-Match header for a save of a record opened at this version (NFR-16). */
export const ifMatch = (version: number): Record<string, string> => ({ "If-Match": `"${version}"` });

export function RefusalNotice({ refusal, onReload }: { refusal: Refusal | undefined; onReload?: () => void }) {
  if (!refusal) return null;
  return (
    <div role="alert" className={`refusal refusal-${refusal.kind}`}>
      <p className="refusal-message">{refusal.message}</p>
      {refusal.kind === "rule" && refusal.rule && (
        <p className="refusal-rule">
          {t("parlour.profile.refused_rule")} <code>{refusal.rule}</code>
        </p>
      )}
      {refusal.kind !== "offline" && <p className="refusal-kept">{t("parlour.profile.entries_kept")}</p>}
      {refusal.kind === "stale" && onReload && (
        <button type="button" onClick={onReload}>
          {t("parlour.profile.reload")}
        </button>
      )}
    </div>
  );
}
